import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { AnimationController, AnimationSnapshot } from '../animation/animationController';
import { CharacterStudio } from '../components/scene/CharacterStudio';
import { ImageLibrary } from '../components/scene/ImageLibrary';
import { LayerPanel } from '../components/scene/LayerPanel';
import { SceneLookPanel } from '../components/scene/LookControls';
import { MotionComposer } from '../components/scene/MotionComposer';
import { ObjectInspector } from '../components/scene/ObjectInspector';
import { PoseInspector } from '../components/scene/PoseInspector';
import { TimelinePanel } from '../components/scene/TimelinePanel';
import { useActiveAnimation, useClockFollowsAnimation, useTransformEdit } from '../components/scene/useAnimationEditing';
import { SceneCanvas, type CanvasApi } from '../components/scene/SceneCanvas';
import { ScenePanel } from '../components/scene/ScenePanel';
import { useSceneEdit } from '../components/scene/useSceneEdit';
import { useMotionPreview } from '../components/scene/useMotionPreview';
import * as S from '../domain/sceneOps';
import { Observable } from '../lib/store';
import { useVisualHistory } from '../state/historyContext';
import { Alert, Button, Panel, inputClass } from '../components/ui';
import { activeScene } from '../domain/factories';
import * as ops from '../domain/projectOps';
import type { Motion, Shape } from '../domain/schemas';
import { useObservable, useObservableSelector } from '../lib/useObservable';
import { useProject } from '../state/projectContext';
import { useServices } from '../state/services';

const SHAPES: Shape[] = ['circle', 'square', 'triangle'];
const MOTIONS: Motion[] = ['orbit', 'pulse', 'bounce', 'spin'];

export function AnimationWorkspace() {
  const { project, update } = useProject();
  const { animation: bus, selection, motion } = useServices();
  const history = useVisualHistory();
  const historyState = useObservable(history.state);
  const scene = activeScene(project);
  // One observable outlives renderer instances so the UI keeps a stable subscription.
  const [state] = useState(() => new Observable<AnimationSnapshot>({ status: 'idle', frame: 0, elapsed: 0 }));
  // Only what this component renders; per-frame values are read by small subscribers below,
  // so playback does not re-render the whole workspace on every frame.
  const status = useObservableSelector(state, (v) => v.status);
  const rendererError = useObservableSelector(state, (v) => v.error);
  const imageErrors = useObservableSelector(state, (v) => v.imageErrors);
  const selected = useObservable(selection);
  const controllerRef = useRef<AnimationController | null>(null);
  const canvasApi = useRef<CanvasApi | null>(null);
  const { edit, error, setError } = useSceneEdit();
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const ready = status === 'paused' || status === 'running';

  // The animation on the timeline drives the clock's timing. A different animation
  // starts at frame 0; editing the current one's duration or fps keeps the playhead.
  const active = useActiveAnimation(project, scene);
  // Motion Composer preview: an uncommitted animation shown on the canvas and timeline.
  const previewing = useObservableSelector(motion, (m) => m.preview && m.steps.length > 0);
  const preview = useMotionPreview(project, scene, active);
  const shown = previewing && preview.animation ? preview.animation : active;
  useClockFollowsAnimation(bus.clock, shown);
  const onTransform = useTransformEdit(edit, scene.id, active, setNotice);

  // Pause while the tab is hidden (saves battery; the clock keeps no time meanwhile) and
  // resume on return if it was playing. Requested playback is never cancelled here.
  useEffect(() => {
    let resume = false;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        resume = bus.clock.get().playing;
        bus.clock.pause();
      } else if (resume) {
        resume = false;
        bus.clock.play();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [bus]);

  // Selection belongs to the active scene: drop ids that no longer exist there.
  useEffect(() => {
    const ids = selection.get().objectIds;
    const kept = ids.filter((id) => scene.objects.some((o) => o.id === id));
    if (kept.length !== ids.length) selection.selectObjects(kept);
  }, [scene, selection]);

  const running = status === 'running';
  const sid = scene.id;
  const single = selected.objectIds.length === 1 ? scene.objects.find((o) => o.id === selected.objectIds[0]) : undefined;
  const posed = single?.type === 'character' ? single : undefined;
  const posedCharacter = posed?.type === 'character' ? project.characters.find((c) => c.id === posed.characterId) : undefined;
  /** Adds an object at the centre of the current view and selects it. */
  const place = (spec: S.NewObject, label: string) => {
    let id = '';
    const centre = canvasApi.current?.viewCentre();
    const ok = edit(label, (p) => {
      const s = S.sceneOf(p, sid);
      const draft = S.buildObject(p, s, spec, { layerId: selection.get().layerId && s.layers.some((l) => l.id === selection.get().layerId) ? selection.get().layerId! : undefined });
      const x = centre ? centre.x - draft.width / 2 : draft.x;
      const y = centre ? centre.y - draft.height / 2 : draft.y;
      const object = { ...draft, x: Math.round(x), y: Math.round(y) };
      id = object.id;
      return S.addObject(p, sid, object);
    });
    if (ok) selection.selectObjects([id]);
  };
  const undo = () => setHistoryError(history.undo());
  const redo = () => setHistoryError(history.redo());

  return (
    <div className="flex flex-col gap-4">
      <ScenePanel scene={scene} edit={edit} />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section aria-labelledby="stage-heading" className="flex flex-col gap-2 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="stage-heading" className="text-sm font-semibold tracking-wide text-zinc-200 uppercase">
              Stage · {scene.name}
            </h2>
            <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Edit">
              <Button size="sm" onClick={undo} disabled={!historyState.canUndo} title={historyState.undoLabel ? `Undo ${historyState.undoLabel} (Ctrl+Z)` : 'Undo'} data-testid="undo">
                ↶ Undo
              </Button>
              <Button size="sm" onClick={redo} disabled={!historyState.canRedo} title={historyState.redoLabel ? `Redo ${historyState.redoLabel} (Ctrl+Shift+Z)` : 'Redo'} data-testid="redo">
                ↷ Redo
              </Button>
              <Button variant="primary" size="sm" onClick={running ? bus.pause : bus.start} disabled={!ready} aria-pressed={running} data-testid="animation-toggle">
                {running ? 'Pause' : 'Start'}
              </Button>
              <Button size="sm" onClick={bus.reset} disabled={!ready} data-testid="animation-reset">
                Reset
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1" role="toolbar" aria-label="Add object">
            {(
              [
                ['Rectangle', { type: 'rect' }],
                ['Circle', { type: 'circle' }],
                ['Ellipse', { type: 'ellipse' }],
                ['Triangle', { type: 'polygon' }],
                ['Text', { type: 'text', text: 'Text' }],
              ] as [string, S.NewObject][]
            ).map(([label, spec]) => (
              <Button key={label} size="sm" onClick={() => place(spec, `Add ${label.toLowerCase()}`)} disabled={!ready} data-testid={`add-${spec.type === 'polygon' ? 'triangle' : spec.type}`}>
                + {label}
              </Button>
            ))}
          </div>
          <SceneCanvas scene={scene} state={state} onEdit={edit} onTransform={onTransform} animation={shown} guides={previewing ? preview.guides : undefined} apiRef={canvasApi} controllerRef={controllerRef} />
          {previewing && (
            <p className="rounded-md border border-sky-900 bg-sky-950/40 px-2 py-1 text-xs text-sky-200" role="status" data-testid="motion-preview-banner">
              Previewing the Motion Composer plan{preview.error ? ' (cannot be applied as is — see the composer)' : ''}: nothing is saved until you press Apply.
            </p>
          )}
          <StatusLine state={state} detail={`${scene.width}×${scene.height} · ${scene.objects.length} objects${active ? ` · ${active.name}` : ''}${previewing ? ' · preview' : ''}`} />
          {(error || historyError) && (
            <Alert title={error ?? historyError ?? ''} action={<Button size="sm" variant="ghost" onClick={() => (setError(null), setHistoryError(null))}>Dismiss</Button>} />
          )}
          {notice && <Alert tone="info" title={notice} action={<Button size="sm" variant="ghost" onClick={() => setNotice(null)}>Dismiss</Button>} />}
          {status === 'error' && <Alert title="Animation is unavailable">{rendererError}</Alert>}
          {imageErrors && imageErrors.length > 0 && (
            <Alert tone="warning" title="Some images could not be loaded">
              {imageErrors.map((id) => project.assets.find((a) => a.id === id)?.name ?? id).join(', ')} — shown as crossed-out placeholders. Re-import or delete them in the image library.
            </Alert>
          )}
          <p className="text-xs text-zinc-400">
            Drag to move · handles resize (Shift keeps proportions) and rotate (Shift snaps 15°) · wheel zooms · Alt/right-drag or one finger on empty space pans · arrows nudge.
          </p>
        </section>
        <div className="flex flex-col gap-4">
          <ObjectInspector scene={scene} edit={edit} onTransform={onTransform} animation={active} frames={state} error={null} />
          {posed && posedCharacter && (
            <PoseInspector key={posed.id} object={posed} character={posedCharacter} animation={active} frames={state} locked={S.isLocked(scene, posed)} onTransform={onTransform} />
          )}
          <LayerPanel scene={scene} edit={edit} />
          <SceneLookPanel scene={scene} edit={edit} />
        </div>
      </div>
      <TimelinePanel project={project} scene={scene} animation={active} edit={edit} frames={state} planned={previewing ? preview.overlay : null} />
      <MotionComposer project={project} scene={scene} active={active} edit={edit} preview={preview} />
      <div className="grid gap-4">
        <CharacterStudio edit={edit} onPlace={(characterId) => place({ type: 'character', characterId }, 'Place character')} />
        <ImageLibrary edit={edit} onPlace={(assetId) => place({ type: 'image', assetId }, 'Add image')} />
      </div>

      {/* Open while the scene has animated shapes, so Phase 0 projects show them as before. */}
      <details className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4" open={scene.elements.length > 0}>
        <summary className="cursor-pointer text-sm font-semibold tracking-wide text-zinc-200 uppercase">Animated shapes ({scene.elements.length})</summary>
        <p className="mt-2 text-xs text-zinc-400">Looping shapes from the original animation scene. They play above the edited scene when you press Start.</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="scene-speed" className="text-sm text-zinc-300">
              Speed {scene.speed.toFixed(2)}×
            </label>
            <input id="scene-speed" type="range" min={0.1} max={5} step={0.05} value={scene.speed} onChange={(e) => update((p) => ops.setSceneSpeed(p, Number(e.target.value)))} />
          </div>
          <AddShapeForm />
        </div>
        {scene.elements.length === 0 ? (
          <p className="mt-3 text-sm text-zinc-400">No animated shapes. Add one above or type “add a red circle that orbits”.</p>
        ) : (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2" aria-label="Scene shapes">
            {scene.elements.map((el, i) => (
              <li key={el.id} className="flex items-center justify-between gap-2 rounded-md bg-zinc-800/60 px-3 py-2 text-sm">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="inline-block size-3 rounded-full" style={{ background: el.color }} />
                  {i + 1}. {el.shape} · {el.motion}
                </span>
                <Button size="sm" variant="ghost" onClick={() => update((p) => ops.removeShape(p, el.id))}>
                  Remove<span className="sr-only"> {el.shape} {i + 1}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  );
}

function AddShapeForm() {
  const { update } = useProject();
  const [shape, setShape] = useState<Shape>('circle');
  const [motion, setMotion] = useState<Motion>('orbit');
  const [color, setColor] = useState('#22d3ee');
  const [error, setError] = useState<string | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    try {
      setError(null);
      update((p) => ops.addShape(p, shape, color, motion));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add shape.');
    }
  };
  return (
    <Panel title="Add shape" headingLevel={3}>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="shape-select" className="text-xs text-zinc-300">
            Shape
          </label>
          <select id="shape-select" className={inputClass} value={shape} onChange={(e) => setShape(e.target.value as Shape)}>
            {SHAPES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="motion-select" className="text-xs text-zinc-300">
            Motion
          </label>
          <select id="motion-select" className={inputClass} value={motion} onChange={(e) => setMotion(e.target.value as Motion)}>
            {MOTIONS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="shape-color" className="text-xs text-zinc-300">
            Colour
          </label>
          <input
            id="shape-color"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-8 w-14 cursor-pointer rounded border border-zinc-700 bg-transparent"
          />
        </div>
        <Button type="submit" variant="primary">
          Add shape
        </Button>
        {error && <p role="alert" className="col-span-2 text-xs text-red-400">{error}</p>}
      </form>
    </Panel>
  );
}

/** Renderer status and playhead (re-renders on every displayed frame; kept tiny). */
function StatusLine({ state, detail }: { state: Observable<AnimationSnapshot>; detail: string }) {
  const s = useObservable(state);
  return (
    <p className="font-mono text-xs text-zinc-400" data-testid="animation-status" data-status={s.status} data-frame={s.frame}>
      {s.status} · frame {s.frame} · {s.elapsed.toFixed(2)}s · {detail}
    </p>
  );
}
